/**
 * Trailblazers (TRL) disruption types.
 *
 * With {@link TrlDisruptionConfig.enabled} on, an opposing player who hits someone
 * with Sparkle Powder WHILE that person is casting the Seismic Excavator/Scanner
 * blocks their scanner: every cast/dig they make is rejected until a TEAMMATE
 * hits them with Sparkle Powder (or an admin clears it). A player may disrupt a
 * given opponent only ONCE per round/node — the node the victim's team is on when
 * the hit lands; a sparkle that lands outside a cast does not use up that chance.
 *
 * "During a cast" is decided server-side from the VICTIM's combat log — their cast
 * start / cancel / completion lines and the incoming sparkle line share one clock
 * — via {@link isHitDuringCast}.
 *
 * The config lives game-level on `games.trl_disruption_config`; records persist in
 * `trl_disruptions` so a server restart frees nobody.
 */
import { TRL_SEISMIC_CAST_MS } from './trlTriangulation';
/** Defaults: off, so existing games are unaffected. */
export const DEFAULT_TRL_DISRUPTION_CONFIG = {
    enabled: false,
};
/** Effective config for a game: authored config, else built-in defaults. */
export function resolveTrlDisruptionConfig(gameConfig) {
    return gameConfig ?? DEFAULT_TRL_DISRUPTION_CONFIG;
}
/** Value-equality across every persisted field. */
export function trlDisruptionConfigEquals(a, b) {
    return a.enabled === b.enabled;
}
/** Sanitise untrusted admin input — normalise, never reject. */
export function normalizeTrlDisruptionConfig(raw) {
    if (!raw || typeof raw !== 'object')
        return { ...DEFAULT_TRL_DISRUPTION_CONFIG };
    const it = raw;
    return { enabled: Boolean(it.enabled) };
}
// ============================================================================
// Cast-window timing
// ============================================================================
/**
 * A cast that was started but whose end never reached the server (lost log line,
 * a too-short cast with no cancel) still counts as open for this long past its
 * nominal {@link TRL_SEISMIC_CAST_MS} length.
 */
export const TRL_DISRUPTION_OPEN_CAST_GRACE_MS = 1000;
/**
 * Did a Sparkle Powder hit at `hitMs` land during one of the casts?
 *
 * - An ENDED cast covers `[startMs, endMs)` — a hit at the very instant of (or
 *   after) the cancel/completion does not count.
 * - An OPEN cast covers `[startMs, startMs + castMs + grace]`.
 */
export function isHitDuringCast(casts, hitMs, castMs = TRL_SEISMIC_CAST_MS) {
    return casts.some((cast) => {
        if (hitMs < cast.startMs)
            return false;
        if (cast.endMs != null)
            return hitMs < cast.endMs;
        return hitMs <= cast.startMs + castMs + TRL_DISRUPTION_OPEN_CAST_GRACE_MS;
    });
}
/** The victim's ACTIVE block, or null. */
export function findActiveTrlBlock(records, playerId) {
    if (!records || !playerId)
        return null;
    return records.find((r) => r.victimPlayerId === playerId && r.clearedAt == null) ?? null;
}
